import { useQuery } from 'react-query';
import { baseApiUrl, fetcher, handleError, ServerErrorResponse } from 'data_sources/constants';
import {
  DraftManual,
  DraftPolicy,
  DraftPreface,
  PoliciesPendingUpdate,
  PolicyUpdate,
  PrefacesPendingUpdate,
  PrefaceUpdate,
  SectionUpdate,
  TransformedPendingUpdates,
} from 'views/App/Portal/Policy/_types/manuals/draft';
import _ from 'lodash';
import ContentConversionHelper from 'views/App/Portal/Policy/DraftManuals/helpers/contentConversionHelper';
import { getDraftPolicy } from './useDraftPolicy';
import { getDraftPreface } from './useDraftPreface';
import draftManualHelper from 'views/App/Portal/Policy/DraftManuals/helpers/draftManualHelper';
import useManageIssuedChangeDecision from 'views/App/Portal/Policy/DraftManuals/Detail/ReviewUpdates/_data_sources/useManageIssuedChangeDecision';

export const getPoliciesPendingUpdates = async (
  manualId: string,
  callback?: (data: PoliciesPendingUpdate[]) => void,
  onError?: (errors: ServerErrorResponse) => void
): Promise<PoliciesPendingUpdate[]> => {
  const url = `${baseApiUrl()}/lexiverse/policies/v1/manuals/${manualId}/issued-change-sets?purpose=DISTRIBUTION`;
  try {
    const data = await fetcher({
      url,
      method: 'get',
      body: {},
    });
    if (callback) callback(data.content as PoliciesPendingUpdate[]);
    return data.content as PoliciesPendingUpdate[];
  } catch (err) {
    if (onError) handleError(err as Error, { onError });
    throw err;
  }
};

export const getPrefacesPendingUpdates = async (
  manualId: string,
  callback?: (data: PrefacesPendingUpdate[]) => void,
  onError?: (errors: ServerErrorResponse) => void
): Promise<PrefacesPendingUpdate[]> => {
  const url = `${baseApiUrl()}/lexiverse/prefaces/v1/manuals/${manualId}/issued-change-sets?purpose=DISTRIBUTION`;
  try {
    const data = await fetcher({
      url,
      method: 'get',
      body: {},
    });
    if (callback) callback(data.content as PrefacesPendingUpdate[]);
    return data.content as PrefacesPendingUpdate[];
  } catch (err) {
    if (onError) handleError(err as Error, { onError });
    throw err;
  }
};

export const getPendingUpdates = async (manual: DraftManual): Promise<TransformedPendingUpdates | null> => {
  const { processIssuedPolicyChangeDecision, processIssuedPrefaceChangeDecision } = useManageIssuedChangeDecision();

  if (!_.isEmpty(manual)) {
    try {
      const result: TransformedPendingUpdates = {
        chapters: {},
        policies: {},
        prefacesUpdated: 0,
        prefaces: {},
      };
      const [prefacesResponse, policiesResponse] = await Promise.all([
        getPrefacesPendingUpdates(manual.id),
        getPoliciesPendingUpdates(manual.id),
      ]);
      const updatedPrefaceIds = _.uniq(prefacesResponse.map((pu) => pu.prefaceId));
      await Promise.all(
        updatedPrefaceIds.map(async (prefaceId) => {
          const currentPreface: DraftPreface = await getDraftPreface(manual.id, prefaceId);

          if (currentPreface) {
            const currentPrefaceUpdates = prefacesResponse.filter((pu) => pu.prefaceId === prefaceId);
            const prefaceTitleUpdate = currentPrefaceUpdates.find((pr) =>
              pr.changes.some((x) => x.change === 'PREFACE_TITLE_EDIT')
            );
            const prefaceContentUpdate = currentPrefaceUpdates.find((pr) =>
              pr.changes.some((x) => x.change === 'PREFACE_CONTENT_EDIT')
            );
            const prefaceUpdate: PrefaceUpdate = {
              count: 0,
              severity: '',
              prefaceTitleUpdate: false,
              prefaceContentUpdate: false,
              currentTitle: currentPreface.title,
              updatedTitle: currentPreface.title,
              currentContent: ContentConversionHelper.contentForRender(currentPreface.content),
              updatedContent: ContentConversionHelper.contentForRender(currentPreface.content),
              changeDate: '',
              updatedDate: currentPreface.updatedAt || '',
              changesetId: currentPrefaceUpdates[0].id,
              currentAttachments: currentPreface.attachments || [],
              updatedAttachments: [],
            };
            if (prefaceTitleUpdate) {
              if (currentPreface && currentPreface.title != prefaceTitleUpdate.title) {
                prefaceUpdate.prefaceTitleUpdate = true;
                prefaceUpdate.updatedTitle = prefaceTitleUpdate.title;
                prefaceUpdate.changeDate = prefaceTitleUpdate.lastDistributionAt;
              } else {
                await new Promise<void>((resolve) =>
                  processIssuedPrefaceChangeDecision(manual.id, prefaceTitleUpdate.id, 'ACCEPT', () => {
                    resolve();
                  })
                );
              }
            }
            if (prefaceContentUpdate) {
              if (
                currentPreface &&
                ContentConversionHelper.contentForRender(currentPreface.content) !=
                  ContentConversionHelper.contentForRender(prefaceContentUpdate.content)
              ) {
                prefaceUpdate.prefaceContentUpdate = true;
                prefaceUpdate.updatedContent = ContentConversionHelper.contentForRender(prefaceContentUpdate.content);
                prefaceUpdate.changeDate = prefaceContentUpdate.lastDistributionAt || new Date().toISOString();
                prefaceUpdate.updatedAttachments = prefaceContentUpdate.attachments || [];
              } else {
                await new Promise<void>((resolve) =>
                  processIssuedPrefaceChangeDecision(manual.id, prefaceContentUpdate.id, 'ACCEPT', () => {
                    resolve();
                  })
                );
              }
            }

            if (prefaceUpdate.prefaceTitleUpdate || prefaceUpdate.prefaceContentUpdate) {
              prefaceUpdate.count = 1;
              prefaceUpdate.severity = currentPrefaceUpdates[0].changeSeverity;
              result.prefaces[prefaceId] = prefaceUpdate;
            }
          }
        })
      );

      result.prefacesUpdated = Object.keys(result.prefaces).length;

      const updatedPolicyIds = _.uniq(policiesResponse.map((pu) => pu.policyId));

      // Find policies with isNew sections or policies marked isNew by traversing the manual
      const policiesWithNewSections = manual.chapters.reduce((acc: string[], chapter) => {
        if (!chapter.policies) return acc;

        chapter.policies.forEach((policy) => {
          const hasNewSections =
            policy.sections?.some((section) => section.isNew) ||
            policy.sections?.some((section) => section.subSections?.some((subsection) => subsection.isNew));
          if (hasNewSections) {
            acc.push(policy.id);
          }
        });
        return acc;
      }, []);

      const policiesThatAreNew = manual.chapters.reduce((acc: string[], chapter) => {
        if (!chapter.policies) return acc;
        chapter.policies.forEach((policy) => {
          if (policy.isNew) {
            acc.push(policy.id);
          }
        });
        return acc;
      }, []);

      // Combine with existing updatedPolicyIds and ensure uniqueness
      const allUpdatedPolicyIds = _.uniq([...updatedPolicyIds, ...policiesWithNewSections, ...policiesThatAreNew]);

      const getPolicyIsNewFromManual = (policyId: string) =>
        manual.chapters.flatMap((c) => c.policies || []).find((p) => p.id === policyId)?.isNew;

      await Promise.all(
        allUpdatedPolicyIds.map(async (policyId) => {
          const currentPolicy: DraftPolicy = await getDraftPolicy(manual.id, policyId);
          const currentPolicyNumber = draftManualHelper.getNumberManualNode(currentPolicy, manual);
          const isPolicyNew = getPolicyIsNewFromManual(policyId) ?? currentPolicy?.isNew;
          if (currentPolicy) {
            const currentPolicyUpdates = policiesResponse.filter((pu) => pu.policyId === policyId);
            const policyTitleUpdate = currentPolicyUpdates.find((pr) =>
              pr.changes.some((x) => x.change === 'POLICY_TITLE_EDIT')
            );
            const policyDisabledUpdate = currentPolicyUpdates.find((pr) =>
              pr.changes.some((x) => x.change === 'POLICY_DISABLED')
            );
            const policyUpdate: PolicyUpdate = {
              count: 0,
              severity: '',
              policyTitleUpdate: false,
              policyDisabled: false,
              currentTitle: currentPolicy.title,
              policyNumber: currentPolicyNumber,
              updatedTitle: currentPolicy.title,
              sections: [],
              changeDate: '',
              updatedDate: currentPolicy.updatedAt || '',
            };

            // Handle POLICY_DISABLED changes first - if policy is disabled, ignore other changes
            if (policyDisabledUpdate) {
              if (currentPolicy && currentPolicy.state !== 'DISABLED') {
                policyUpdate.policyDisabled = true;
                policyUpdate.policyDisabledChangesetId = policyDisabledUpdate.id;
                policyUpdate.changeDate = policyDisabledUpdate.lastDistributionAt;
              } else {
                await new Promise<void>((resolve) =>
                  processIssuedPolicyChangeDecision(manual.id, policyDisabledUpdate.id, 'ACCEPT', () => {
                    resolve();
                  })
                );
              }
            }

            // Only process other changes if policy is not disabled
            if (!policyUpdate.policyDisabled) {
              if (policyTitleUpdate) {
                if (currentPolicy && currentPolicy.title != policyTitleUpdate.title) {
                  policyUpdate.policyTitleUpdate = true;
                  policyUpdate.currentTitle = currentPolicy.title;
                  policyUpdate.updatedTitle = policyTitleUpdate.title;
                  policyUpdate.titleChangesetId = policyTitleUpdate.id;
                  policyUpdate.changeDate = policyTitleUpdate.lastDistributionAt;
                } else {
                  await new Promise<void>((resolve) =>
                    processIssuedPolicyChangeDecision(manual.id, policyTitleUpdate.id, 'ACCEPT', () => {
                      resolve();
                    })
                  );
                }
              }
            }

            // Only process sections if policy is not disabled
            if (!policyUpdate.policyDisabled) {
              currentPolicy.sections.forEach((section) => {
                const sectionNumber = draftManualHelper.getNumberPolicyNode(section, currentPolicy);
                const sectionUpdate: SectionUpdate = {
                  id: section.id,
                  sectionTitleUpdate: false,
                  sectionContentUpdate: false,
                  sectionEditLevelUpdate: false,
                  stateChange: false,
                  updatedState: section.isNew ? 'ENABLED' : '',
                  currentState: section.state,
                  currentEditLevelTag: section.editLevelTag || '',
                  updatedEditLevelTag: section.editLevelTag || '',
                  currentTitle: section.title,
                  updatedTitle: section.title,
                  sectionNumber: `${currentPolicyNumber}.${sectionNumber}`,
                  currentContent: ContentConversionHelper.contentForRender(section.content),
                  updatedContent: ContentConversionHelper.contentForRender(section.content),
                  changesetIds: [],
                  changeDate: section.isNew ? currentPolicy.updatedAt || '' : '',
                  updatedDate: currentPolicy.updatedAt || '',
                  isSubsection: false,
                  isNew: section.isNew,
                  currentAttachments: section.attachments || [],
                  updatedAttachments: [],
                };
                currentPolicyUpdates.forEach(async (update) => {
                  const isSectionDisabled = update.changes.some(
                    (x) => x.change === 'SECTION_DISABLED' && update.sections.some((s) => s.id === section.id)
                  );
                  if (isSectionDisabled) {
                    if (section.state !== 'DISABLED') {
                      sectionUpdate.stateChange = true;
                      sectionUpdate.updatedState = 'DISABLED';
                      sectionUpdate.currentState = section.state;
                      sectionUpdate.changesetIds.push(update.id);
                      sectionUpdate.changeDate = update.lastDistributionAt;
                    } else {
                      await new Promise<void>((resolve) =>
                        processIssuedPolicyChangeDecision(manual.id, update.id, 'ACCEPT', () => {
                          resolve();
                        })
                      );
                    }
                  }

                  if (update.changes.some((x) => x.change === 'SECTION_TITLE_EDIT')) {
                    update.sections.forEach((s) => {
                      if (s.id === section.id) {
                        sectionUpdate.sectionTitleUpdate = true;
                        sectionUpdate.currentTitle = section.title;
                        sectionUpdate.updatedTitle = s.title;
                        sectionUpdate.changesetIds.push(update.id);
                        sectionUpdate.changeDate = update.lastDistributionAt;
                        sectionUpdate.updatedAttachments = s.attachments || [];
                      }
                    });
                  }
                  if (update.changes.some((x) => x.change === 'SECTION_CONTENT_EDIT')) {
                    update.sections.forEach(async (s) => {
                      if (s.id === section.id) {
                        if (
                          ContentConversionHelper.contentForRender(section.content) !==
                          ContentConversionHelper.contentForRender(s.content)
                        ) {
                          sectionUpdate.sectionContentUpdate = true;
                          sectionUpdate.updatedTitle = s.title;
                          sectionUpdate.currentContent = ContentConversionHelper.contentForRender(section.content);
                          sectionUpdate.updatedContent = ContentConversionHelper.contentForRender(s.content);
                          sectionUpdate.changesetIds.push(update.id);
                          sectionUpdate.changeDate = update.lastDistributionAt;
                          sectionUpdate.updatedAttachments = s.attachments || [];
                        } else {
                          await new Promise<void>((resolve) =>
                            processIssuedPolicyChangeDecision(manual.id, update.id, 'ACCEPT', () => {
                              resolve();
                            })
                          );
                        }
                      }
                    });
                  }
                  if (update.changes.some((x) => x.change === 'SECTION_EDIT_LEVEL_TAG_EDIT')) {
                    update.sections.forEach((s) => {
                      if (s.id === section.id) {
                        if (section.editLevelTag !== s.editLevelTag) {
                          sectionUpdate.sectionEditLevelUpdate = true;
                          sectionUpdate.currentEditLevelTag = section.editLevelTag || '';
                          sectionUpdate.updatedEditLevelTag = s.editLevelTag || '';
                          sectionUpdate.changesetIds.push(update.id);
                          sectionUpdate.changeDate = update.lastDistributionAt;
                        } else {
                          processIssuedPolicyChangeDecision(manual.id, update.id, 'ACCEPT', () => {});
                        }
                      }
                    });
                  }
                });
                if (
                  sectionUpdate.sectionTitleUpdate ||
                  sectionUpdate.sectionContentUpdate ||
                  sectionUpdate.sectionEditLevelUpdate ||
                  sectionUpdate.stateChange ||
                  sectionUpdate.isNew
                ) {
                  policyUpdate.sections.push(sectionUpdate);
                }
                section.subSections.forEach((subsection) => {
                  const sectionNumber = draftManualHelper.getNumberPolicyNode(subsection, currentPolicy);
                  const sectionUpdate: SectionUpdate = {
                    id: subsection.id,
                    sectionTitleUpdate: false,
                    sectionContentUpdate: false,
                    sectionEditLevelUpdate: false,
                    currentEditLevelTag: subsection.editLevelTag || '',
                    updatedEditLevelTag: subsection.editLevelTag || '',
                    stateChange: false,
                    updatedState: subsection.isNew ? 'ENABLED' : '',
                    currentState: subsection.state,
                    currentTitle: subsection.title,
                    sectionNumber: `${currentPolicyNumber}.${sectionNumber}`,
                    updatedTitle: subsection.title,
                    currentContent: ContentConversionHelper.contentForRender(subsection.content),
                    updatedContent: ContentConversionHelper.contentForRender(subsection.content),
                    changesetIds: [],
                    changeDate: subsection.isNew ? currentPolicy.updatedAt || '' : '',
                    updatedDate: currentPolicy.updatedAt || '',
                    isSubsection: true,
                    isNew: subsection.isNew,
                    currentAttachments: subsection.attachments || [],
                    updatedAttachments: [],
                  };
                  currentPolicyUpdates.forEach((update) => {
                    if (update.changes.some((x) => x.change === 'SUBSECTION_TITLE_EDIT')) {
                      update.sections.forEach((s) => {
                        s.subSections.forEach((ss: any) => {
                          if (ss.id === subsection.id) {
                            sectionUpdate.sectionTitleUpdate = true;
                            sectionUpdate.currentTitle = subsection.title;
                            sectionUpdate.updatedTitle = ss.title;
                            sectionUpdate.changesetIds.push(update.id);
                            sectionUpdate.changeDate = update.lastDistributionAt;
                            sectionUpdate.updatedAttachments = ss.attachments || [];
                          }
                        });
                      });
                    }

                    if (update.changes.some((x) => x.change === 'SUBSECTION_DISABLED')) {
                      update.sections.forEach((s) => {
                        if (s.subSections) {
                          s.subSections.forEach((ss: any) => {
                            if (ss.id === subsection.id && ss.state === 'DISABLED') {
                              sectionUpdate.stateChange = true;
                              sectionUpdate.updatedState = 'DISABLED';
                              sectionUpdate.currentState = subsection.state;
                              sectionUpdate.changesetIds.push(update.id);
                              sectionUpdate.changeDate = update.lastDistributionAt;
                            }
                          });
                        }
                      });
                    }

                    if (update.changes.some((x) => x.change === 'SUBSECTION_CONTENT_EDIT')) {
                      update.sections.forEach((s) => {
                        s.subSections.forEach(async (ss: any) => {
                          if (ss.id === subsection.id) {
                            if (
                              ContentConversionHelper.contentForRender(subsection.content) !==
                              ContentConversionHelper.contentForRender(ss.content)
                            ) {
                              sectionUpdate.sectionContentUpdate = true;
                              sectionUpdate.updatedTitle = ss.title;
                              sectionUpdate.currentContent = ContentConversionHelper.contentForRender(
                                subsection.content
                              );
                              sectionUpdate.updatedContent = ContentConversionHelper.contentForRender(ss.content);
                              sectionUpdate.changesetIds.push(update.id);
                              sectionUpdate.changeDate = update.lastDistributionAt;
                              sectionUpdate.updatedAttachments = ss.attachments || [];
                            } else {
                              await new Promise<void>((resolve) =>
                                processIssuedPolicyChangeDecision(manual.id, update.id, 'ACCEPT', () => {
                                  resolve();
                                })
                              );
                            }
                          }
                        });
                      });
                    }
                    if (update.changes.some((x) => x.change === 'SUBSECTION_EDIT_LEVEL_TAG_EDIT')) {
                      update.sections.forEach((s) => {
                        s.subSections.forEach((ss: any) => {
                          if (ss.id === subsection.id) {
                            if (subsection.editLevelTag !== ss.editLevelTag) {
                              sectionUpdate.sectionEditLevelUpdate = true;
                              sectionUpdate.currentEditLevelTag = subsection.editLevelTag || '';
                              sectionUpdate.updatedEditLevelTag = ss.editLevelTag || '';
                              sectionUpdate.changesetIds.push(update.id);
                              sectionUpdate.changeDate = update.lastDistributionAt;
                            } else {
                              processIssuedPolicyChangeDecision(manual.id, update.id, 'ACCEPT', () => {});
                            }
                          }
                        });
                      });
                    }
                  });
                  if (
                    sectionUpdate.sectionTitleUpdate ||
                    sectionUpdate.sectionContentUpdate ||
                    sectionUpdate.sectionEditLevelUpdate ||
                    sectionUpdate.stateChange ||
                    sectionUpdate.isNew
                  ) {
                    policyUpdate.sections.push(sectionUpdate);
                  }
                });
              });
            }
            if (
              policyUpdate.policyTitleUpdate ||
              policyUpdate.policyDisabled ||
              policyUpdate.sections.length > 0 ||
              isPolicyNew
            ) {
              policyUpdate.count = policyUpdate.sections.length;
              if (policyUpdate.policyTitleUpdate) policyUpdate.count += 1;
              if (policyUpdate.policyDisabled) policyUpdate.count += 1;
              if (isPolicyNew) policyUpdate.count = Math.max(policyUpdate.count, 1);
              policyUpdate.isNewPolicyOnly =
                isPolicyNew &&
                !policyUpdate.policyTitleUpdate &&
                !policyUpdate.policyDisabled &&
                policyUpdate.sections.length === 0;
              policyUpdate.severity = currentPolicyUpdates[0]?.changeSeverity || 'MINOR';
              result.policies[policyId] = policyUpdate;

              // Find the chapter ID for this policy
              let chapterId = currentPolicyUpdates[0]?.chapterId;
              if (!chapterId) {
                // If no chapterId from updates, find it from the manual structure
                for (const chapter of manual.chapters) {
                  if (chapter.policies.some((p) => p.id === policyId)) {
                    chapterId = chapter.id;
                    break;
                  }
                }
              }

              if (chapterId) {
                result.chapters[chapterId] ||= 0;
                result.chapters[chapterId] += 1;
              }
            }
          }
        })
      );

      return result;
    } catch (err) {
      console.warn('getPendingUpdates:err', err);
      throw err;
    }
  } else {
    return null;
  }
};

export default function usePendingUpdates(manual?: DraftManual | null | undefined, options = {}) {
  return useQuery<TransformedPendingUpdates | null, Error>(
    ['draft_manual_pending_updates', manual?.id],
    async () => {
      const data = await getPendingUpdates(manual!);
      return data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 60, // 2 min
      cacheTime: 1000 * 60 * 60,
      keepPreviousData: true,
      ...options,
    }
  );
}
